import { Button, Flex, ProgressBar, Tooltip, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import { GrantProgramCardProps } from './GrantProgramCard.types'
import { useNavigate } from 'react-router-dom'
import { memo } from 'react'

import { useLocale } from '@percent/cause-dashboard/common/hooks/useLocale/useLocale'
import { formatDate } from '@percent/cause-dashboard/common/utility/date'
import { atlassianIcon, canvaLogo, fallbackLogo, lululemonIcon, nikeIcon } from '@percent/cause-dashboard/assets/images'
import styles from './GrantProgramCard.module.scss'

// TODO: remove once we have added partner icons in DB and replace logo with icon
const LULULEMON_PARTNER_IDS = ['sandbox_partner_000000DDoqH2VO0Qx8JC2vQzzq31c', 'partner_000000DDor9UhA7RWU1u6s9I6me6C']
const NIKE_PARTNER_IDS = ['sandbox_partner_000000DKEcYShrOTBWrQC0u7Bzb6G', 'partner_000000DKEcdDmoHwgLnSo9DJxh4gC']
const ATLASSIAN_PARTNER_IDS = ['sandbox_partner_000000C7YyeejqAcsPa4LvnVzXupU', 'partner_000000CBRzAHRM9WxOQCLvDOxYEIC']
const CANVA_PARTNER_IDS: string[] = [
  'sandbox_partner_000000CK9llgraz1Ye9EzfP18y0BM',
  'partner_000000CMTqjAZ1lf21cvt6GyJnUI4'
]

const PARTNER_ICONS: { ids: string[]; icon: string }[] = [
  { ids: LULULEMON_PARTNER_IDS, icon: lululemonIcon },
  { ids: NIKE_PARTNER_IDS, icon: nikeIcon },
  { ids: ATLASSIAN_PARTNER_IDS, icon: atlassianIcon },
  { ids: CANVA_PARTNER_IDS, icon: canvaLogo }
]

const getPartnerLogo = (partnerId: string, logo: string | null) =>
  PARTNER_ICONS.find(({ ids }) => ids.includes(partnerId))?.icon ?? logo ?? fallbackLogo

export const GrantProgramCard = memo(({ id, program, tasks, nominatorGroups }: Readonly<GrantProgramCardProps>) => {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const navigate = useNavigate()

  const {
    id: programId,
    name: programName,
    budgetMin,
    budgetMax,
    currency,
    partner: { id: partnerId, logo }
  } = program

  const logoSrc = getPartnerLogo(partnerId, logo)

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency ?? 'USD' }).format(amount)

  const navigateToTasks = () => {
    navigate(`/grants/${id}`)
  }

  const closestIncompleteTask = tasks
    .filter(task => task.status !== 'done' && task.dueByDate)
    .sort((a, b) => {
      const aTime = a.dueByDate ? new Date(a.dueByDate).getTime() : 0
      const bTime = b.dueByDate ? new Date(b.dueByDate).getTime() : 0
      return aTime - bTime
    })[0]

  const deadline = closestIncompleteTask?.dueByDate

  const formatDeadline = (date: Date | null) => {
    return date ? formatDate(date, locale) : t('grants.grantProgramCard.notAvailable')
  }

  const tasksCompleted = tasks.filter(task => task.status === 'done').length
  const totalTasks = tasks.length
  const progress = Math.floor((tasksCompleted / totalTasks) * 100)
  const isBudgetDefined = budgetMin !== null && budgetMax !== null

  const hasActiveDeadline = deadline && tasksCompleted < totalTasks
  const hasTasks = totalTasks > 0
  const allTasksCompleted = hasTasks && tasksCompleted === totalTasks
  const nominatorCount = nominatorGroups?.length ?? 0

  const getButtonLabel = () => {
    if (allTasksCompleted) {
      return t('grants.grantProgramCard.viewCompletedTasks')
    }
    if (hasTasks) {
      return t('grants.grantProgramCard.viewTasks')
    }
    return t('grants.grantProgramCard.viewDetails')
  }
  const hasNominatorGroups = nominatorCount > 0

  return (
    <Flex className={styles.grantProgramCard} data-testid={`grant-${programId}-card`}>
      <Flex className={styles.logoWrapper}>
        <img src={logoSrc} alt={programName} style={{ objectFit: 'contain' }} />
      </Flex>
      <Typography variant="h4">{programName}</Typography>
      {isBudgetDefined && (
        <Typography variant="small">
          {t('grants.grantProgramCard.donationRange', {
            min: formatCurrency(budgetMin ?? 0),
            max: formatCurrency(budgetMax ?? 0)
          })}
        </Typography>
      )}
      {hasNominatorGroups && (
        <Typography variant="small">
          {t('grants.grantProgramCard.nominatedBy')} {nominatorGroups?.[0]}
          {nominatorCount > 1 &&
            t('grants.grantProgramCard.nominatedByOthers', {
              count: nominatorCount - 1
            })}
        </Typography>
      )}
      <Flex direction="column" mt="auto" gap={8}>
        <Flex direction="column" align="flex-start" gap={2} pb={12}>
          <Typography variant="small" $color="goodstack">
            {hasTasks ? (
              tasksCompleted === totalTasks ? (
                <Tooltip content={t('grants.grantProgramCard.tooltip.allTasksCompleted')} placement="top">
                  {t('grants.grantProgramCard.tasksCompleted', {
                    tasksCompleted: tasksCompleted,
                    totalTasks: totalTasks
                  })}
                </Tooltip>
              ) : (
                t('grants.grantProgramCard.tasksCompleted', {
                  tasksCompleted: tasksCompleted,
                  totalTasks: totalTasks
                })
              )
            ) : (
              <Tooltip content={t('grants.grantProgramCard.tooltip.noTasksYet')} placement="top-start">
                {t('grants.grantProgramCard.noTasksYet')}
              </Tooltip>
            )}
          </Typography>
          <ProgressBar
            progressPercentage={progress}
            shadeColor="gray70"
            backgroundColorComplete="goodstack"
            backgroundColorIncomplete="goodstack"
            sizeVariant="x-small"
          />
          <Typography $color="gray400" style={{ fontSize: '12px' }}>
            {hasActiveDeadline
              ? t('grants.grantProgramCard.upcomingDeadline', { deadline: formatDeadline(new Date(deadline)) })
              : t('grants.grantProgramCard.noUpcomingDeadline')}
          </Typography>
        </Flex>
        <Button variant={hasActiveDeadline ? 'primary' : 'secondary'} size="small" onClick={navigateToTasks}>
          {getButtonLabel()}
        </Button>
      </Flex>
    </Flex>
  )
})
